import { DeleteOutlined, EditOutlined, UserAddOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch, withQuery } from "../api/client";
import { compareText, formatDate, formatDateTime } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  InvitationCircumscriptionRow,
  InvitationCongregationRow,
  InvitationUserRow,
  ListResponse,
  NotificationsResponse,
  RoleAssignationRow,
  SentInvitationRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";

const API_DATE = "YYYY-MM-DD";

interface NewUserFormValues {
  firstname: string;
  lastname: string;
  email: string;
  birthday?: dayjs.Dayjs;
  baptism?: dayjs.Dayjs;
  valid_from?: dayjs.Dayjs;
}

interface RoleFormValues {
  valid_from?: dayjs.Dayjs;
  observer: boolean;
  observations: string;
}

/**
 * Sidebar entry "assign_new_role" → /invitation/assign_new_role.
 *
 * Giving somebody a role is an invitation, not an assignment: the invited
 * person confirms it on their own /confirm-changes screen before it takes
 * effect. This screen is the chain that leads there — find the person, pick
 * where, then say which role.
 *
 * It gathers what the old application spread over three views: the role
 * assignment grid, the "invite a new user" form behind its toolbar, and the
 * list of invitations already sent. The last one had no sidebar entry of its
 * own (the line is commented out in `get_tabs`), so it lives here as a tab.
 */
export function AssignRolePage() {
  const { t } = useTranslation();

  return (
    <Card title={t("navigation.assign_new_role")}>
      <Tabs
        items={[
          { key: "assign", label: t("navigation.assign_new_role"), children: <UserSearch /> },
          {
            key: "sent",
            label: t("navigation.sent_invitations"),
            children: <SentInvitations />,
          },
        ]}
      />
    </Card>
  );
}

/** Shared error handling — every mutation on this screen wants the same. */
function useMutationError() {
  const notify = useServerNotifications();
  const { message } = App.useApp();

  return useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );
}

/**
 * Spelled out rather than assembled from the role type: check-i18n.mjs only
 * sees literal keys.
 */
function useRoleName() {
  const { t } = useTranslation();

  return useCallback(
    (type: string) => {
      if (type === "speaker") return t("role.speaker");
      if (type === "local_coordinator") return t("role.local_coordinator");
      if (type === "zone_coordinator") return t("role.zone_coordinator");
      if (type === "web_master") return t("role.web_master");
      return type;
    },
    [t],
  );
}

function UserSearch() {
  const { t } = useTranslation();
  const roleName = useRoleName();
  const notify = useServerNotifications();
  const { modal } = App.useApp();
  const queryClient = useQueryClient();
  const showError = useMutationError();
  const [newUserForm] = Form.useForm<NewUserFormValues>();

  const [filters, setFilters] = useState<FilterValues>({});
  const [inviting, setInviting] = useState(false);
  const [assigningTo, setAssigningTo] = useState<InvitationUserRow | null>(null);

  // Without a search the endpoint answers with the pending invitations only —
  // it does not list every user of the installation.
  const users = useQuery({
    queryKey: ["invitation", "users", filters],
    queryFn: () => apiFetch<ListResponse<InvitationUserRow>>(withQuery("/invitation/users", filters)),
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["invitation"] });
  }, [queryClient]);

  const invite = useMutation({
    mutationFn: (values: NewUserFormValues) =>
      apiFetch<NotificationsResponse>("/invitation/invite_user", {
        method: "POST",
        body: {
          firstname: values.firstname,
          lastname: values.lastname,
          email: values.email,
          birthday: values.birthday?.format(API_DATE),
          baptism: values.baptism?.format(API_DATE),
          valid_from: values.valid_from?.format(API_DATE),
        },
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      setInviting(false);
      refresh();
    },
    onError: showError,
  });

  const cancel = useMutation({
    mutationFn: (id: number | string) =>
      apiFetch<NotificationsResponse>(`/invitation/${id}`, { method: "DELETE" }),
    onSuccess: (data) => {
      notify(data.notifications);
      refresh();
    },
    onError: showError,
  });

  const fields = useMemo(
    () => [
      { name: "first_name", label: t("activerecord.attributes.invitation.name") },
      { name: "last_name", label: t("activerecord.attributes.invitation.lastname") },
    ],
    [t],
  );

  // What a single role tag says. Shared so the export cannot drift away from
  // what the screen shows.
  const roleLabel = useCallback(
    (role: InvitationUserRow["roles"][number]) =>
      [roleName(role.type), role.circumscription_code, role.congregation]
        .filter(Boolean)
        .join(" · "),
    [roleName],
  );

  const statusLabel = useCallback(
    (row: InvitationUserRow) => {
      if (row.type === "pending_invitation") return row.status ?? "";
      return row.invitation_count > 0
        ? `${t("invitation.status_invited")} (${row.invitation_count})`
        : "";
    },
    [t],
  );

  // Two of the columns render something the export cannot read off the row —
  // the stacked role tags have no dataIndex at all, and the status is assembled
  // from two fields — so both say explicitly what they contribute.
  const columns = useMemo<ExportableColumn<InvitationUserRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.invitation.name"),
        dataIndex: "name",
        width: 160,
        sorter: (a, b) => compareText(a.name, b.name),
      },
      {
        title: t("activerecord.attributes.invitation.lastname"),
        dataIndex: "lastname",
        width: 160,
        sorter: (a, b) => compareText(a.lastname, b.lastname),
      },
      {
        title: t("activerecord.attributes.role.roletype"),
        key: "roles",
        // Which role, and only then where — a bare "CH01 · Bern" says nothing
        // about what the person is there. The old grid had the same three
        // captions side by side, one column each.
        //
        // A registered user carries no address here (invitation#users answers
        // with none), so this is also where the pending invitations put theirs,
        // as the old grid did: they have no roles yet, and the address is what
        // identifies them until the account exists.
        render: (_value, row) =>
          row.type === "pending_invitation" ? (
            <Typography.Text type="secondary">{row.email}</Typography.Text>
          ) : (
            // One role per line: side by side they add up to a column wide
            // enough to put the whole table behind a scrollbar, and the old
            // grid stacked them too (one htmlline per role).
            <Space orientation="vertical" size={2} align="start">
              {row.roles.map((role) => (
                <Tag
                  key={`${role.type}-${role.circumscription_code}-${role.congregation}`}
                  style={{ marginInlineEnd: 0 }}
                >
                  {roleLabel(role)}
                </Tag>
              ))}
            </Space>
          ),
        exportValue: (row) =>
          row.type === "pending_invitation"
            ? (row.email ?? "")
            : // One cell, so the lines the screen stacks are joined instead.
              row.roles.map(roleLabel).join("; "),
      },
      {
        title: t("activerecord.attributes.invitation.status"),
        dataIndex: "status",
        width: 200,
        ellipsis: true,
        // Only a pending new-user invitation carries a status of its own. For a
        // registered account the row says how many invitations the caller has
        // sent them and not got an answer to — the old grid put that number in
        // front of the first name ("Inviti: 2"). Without an open invitation
        // there is nothing to report about an existing account.
        render: (_value: string | null, row) => {
          const label = statusLabel(row);
          return label ? <Tag color="blue">{label}</Tag> : null;
        },
        exportValue: statusLabel,
      },
      {
        key: "actions",
        fixed: "right",
        width: 110,
        render: (_value, row) => (
          <Space size={4}>
            {/* Only a registered account can be given a role — a pending
                invitation has no user behind it yet. */}
            {row.type === "user" && (
              <Tooltip title={t("navigation.assign_new_role")}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setAssigningTo(row)}
                />
              </Tooltip>
            )}
            {(row.type === "pending_invitation" || row.invitation_count > 0) && (
              <Tooltip title={t("invitation.cancel")}>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={cancel.isPending}
                  onClick={() =>
                    modal.confirm({
                      title: t("dialog.confirmation_title"),
                      content: t("invitation.confirm_cancel_invitation"),
                      okText: t("dialog.answer_yes"),
                      okButtonProps: { danger: true },
                      cancelText: t("dialog.answer_no"),
                      onOk: () => cancel.mutate(row.id),
                    })
                  }
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, cancel, modal, roleLabel, statusLabel],
  );

  return (
    <>
      <Space align="start" style={{ display: "flex", justifyContent: "space-between" }}>
        <FilterBar fields={fields} onApply={setFilters} loading={users.isFetching} />
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => {
            newUserForm.resetFields();
            setInviting(true);
          }}
        >
          {t("invitation.new_user")}
        </Button>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title={t("invitation.choose_circumscription_congregation")}
      />

      <DataTable<InvitationUserRow>
        columns={columns}
        rows={users.data?.data}
        loading={users.isPending || cancel.isPending}
        error={users.error}
        exportAs={t("navigation.assign_new_role")}
        rowKey="id"
      />

      <Modal
        open={inviting}
        title={t("invitation.new_user")}
        onCancel={() => setInviting(false)}
        okText={t("invitation.create_new_user")}
        okButtonProps={{ loading: invite.isPending }}
        onOk={() => newUserForm.submit()}
        destroyOnHidden
      >
        <Form<NewUserFormValues>
          form={newUserForm}
          layout="vertical"
          onFinish={(values) => invite.mutate(values)}
        >
          <Form.Item
            name="firstname"
            label={t("activerecord.attributes.invitation.name")}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lastname"
            label={t("activerecord.attributes.invitation.lastname")}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={t("activerecord.attributes.invitation.email")}
            rules={[{ required: true }, { type: "email" }]}
          >
            <Input />
          </Form.Item>
          {/* Both optional: the two are condensed into the discriminator that
              tells two people of the same name apart. */}
          <Form.Item name="birthday" label={t("activerecord.attributes.invitation.birthday")}>
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="baptism" label={t("activerecord.attributes.invitation.baptism")}>
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="valid_from" label={t("activerecord.attributes.invitation.valid_from")}>
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
        </Form>
      </Modal>

      {assigningTo && (
        <AssignDrawer user={assigningTo} onClose={() => setAssigningTo(null)} />
      )}
    </>
  );
}

/**
 * Pick the place, then the role. The circumscription list is sorted by pending
 * invitations first, so wherever this user already has something on the way is
 * at the top.
 */
function AssignDrawer({
  user,
  onClose,
}: {
  user: InvitationUserRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const queryClient = useQueryClient();
  const showError = useMutationError();
  const [roleForm] = Form.useForm<RoleFormValues>();

  const [circumscriptionId, setCircumscriptionId] = useState<number | null>(null);
  const [congregationId, setCongregationId] = useState<number | null>(null);
  const [editing, setEditing] = useState<RoleAssignationRow | null>(null);

  const circumscriptions = useQuery({
    queryKey: ["invitation", "circumscriptions", user.id],
    queryFn: () =>
      apiFetch<ListResponse<InvitationCircumscriptionRow>>(
        `/invitation/users/${user.id}/circumscriptions`,
      ),
  });

  const congregations = useQuery({
    queryKey: ["invitation", "congregations", user.id, circumscriptionId],
    queryFn: () =>
      apiFetch<ListResponse<InvitationCongregationRow>>(
        `/invitation/users/${user.id}/circumscriptions/${circumscriptionId}/congregations`,
      ),
    enabled: circumscriptionId !== null,
  });

  const roles = useQuery({
    queryKey: ["invitation", "roles", user.id, circumscriptionId, congregationId],
    queryFn: () =>
      apiFetch<ListResponse<RoleAssignationRow>>(
        withQuery(`/invitation/users/${user.id}/roles`, {
          circumscription_id: circumscriptionId,
          congregation_id: congregationId,
        }),
      ),
    enabled: circumscriptionId !== null,
  });

  const refreshRoles = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["invitation"] });
  }, [queryClient]);

  const assign = useMutation({
    mutationFn: ({ row, values }: { row: RoleAssignationRow; values: RoleFormValues }) =>
      apiFetch<NotificationsResponse>(`/invitation/users/${user.id}/roles/${row.role_type}`, {
        method: "PUT",
        body: {
          circumscription_id: circumscriptionId,
          congregation_id: congregationId,
          invitation_id: row.invitation_id,
          valid_from: values.valid_from?.format(API_DATE),
          observer: values.observer,
          observations: values.observations,
        },
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      refreshRoles();
    },
    onError: showError,
  });

  const remove = useMutation({
    mutationFn: (row: RoleAssignationRow) =>
      apiFetch<NotificationsResponse>(`/invitation/users/${user.id}/roles/${row.role_type}`, {
        method: "DELETE",
        body: {
          circumscription_id: circumscriptionId,
          congregation_id: congregationId,
          invitation_id: row.invitation_id,
        },
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      refreshRoles();
    },
    onError: showError,
  });

  const roleName = useRoleName();

  const busy = assign.isPending || remove.isPending;

  const placeColumns = <T extends { invitation_count: number; denomination: string | null }>(
    codeTitle: string,
    codeKey: string,
  ): NonNullable<TableProps<T>["columns"]> => [
    { title: codeTitle, dataIndex: codeKey, width: 90 },
    { title: t("activerecord.attributes.circumscription.denomination"), dataIndex: "denomination", ellipsis: true },
    {
      title: t("navigation.sent_invitations"),
      dataIndex: "invitation_count",
      width: 110,
      align: "right",
      render: (value: number) => (value > 0 ? <Tag color="blue">{value}</Tag> : null),
    },
  ];

  const roleColumns = useMemo<ExportableColumn<RoleAssignationRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.role.roletype"),
        dataIndex: "role_type_label",
        width: 200,
        render: (value: string) => roleName(value),
        exportValue: (row) => roleName(row.role_type_label),
      },
      {
        title: t("activerecord.attributes.invitation.status"),
        dataIndex: "assigned",
        width: 140,
        // Both states block a new assignment, which is why the API reports them
        // as one flag — but they are not the same thing to read: an invitation
        // is still waiting for its answer, a role is already in force.
        render: (value: boolean, row) =>
          row.active ? (
            <Tag color="green">{t("invitation.status_active")}</Tag>
          ) : value ? (
            <Tag color="blue">{t("invitation.status_invited")}</Tag>
          ) : null,
        exportValue: (row) =>
          row.active
            ? t("invitation.status_active")
            : row.assigned
              ? t("invitation.status_invited")
              : null,
      },
      {
        title: t("activerecord.attributes.invitation.valid_from"),
        dataIndex: "valid_from",
        width: 130,
        render: (value: string | null) => formatDate(value),
      },
      {
        title: t("activerecord.attributes.invitation.observer"),
        dataIndex: "observer",
        width: 110,
        render: (value: boolean | null) => (value ? <Tag>{t("role.observer")}</Tag> : null),
        exportValue: (row) => (row.observer ? t("role.observer") : null),
      },
      {
        title: t("activerecord.attributes.invitation.observations"),
        dataIndex: "observations",
        ellipsis: true,
      },
      {
        key: "actions",
        fixed: "right",
        width: 110,
        render: (_value, row) => {
          if (row.readonly) {
            // The server says why — no point offering an action that is
            // certain to be declined.
            return (
              <Tooltip title={row.readonly_reason ?? t("invitation.cannot_be_changed")}>
                <Typography.Text type="secondary">—</Typography.Text>
              </Tooltip>
            );
          }

          return (
            <Space size={4}>
              <Tooltip title={row.assigned ? t("form.update") : t("invitation.invite")}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled={busy || !row.editable}
                  onClick={() => {
                    setEditing(row);
                    roleForm.resetFields();
                    roleForm.setFieldsValue({
                      valid_from: row.valid_from ? dayjs(row.valid_from) : undefined,
                      observer: row.observer ?? false,
                      observations: row.observations ?? "",
                    });
                  }}
                />
              </Tooltip>
              {row.assigned && (
                <Tooltip title={t("invitation.cancel")}>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={busy}
                    onClick={() => remove.mutate(row)}
                  />
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ],
    [t, busy, roleName, roleForm, remove],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={1100}
      destroyOnHidden
      title={[user.name, user.lastname].filter(Boolean).join(" ")}
    >
      <Typography.Title level={5}>{t("invitation.choose_circumscription")}</Typography.Title>
      <Table<InvitationCircumscriptionRow>
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ y: 200 }}
        loading={circumscriptions.isPending}
        dataSource={circumscriptions.data?.data ?? []}
        columns={placeColumns<InvitationCircumscriptionRow>(
          t("activerecord.attributes.circumscription.circumscription_code"),
          "circumscription_code",
        )}
        rowClassName={(row) => (row.id === circumscriptionId ? "ant-table-row-selected" : "")}
        onRow={(row) => ({
          onClick: () => {
            setCircumscriptionId(row.id);
            setCongregationId(null);
          },
          style: { cursor: "pointer" },
        })}
      />

      {circumscriptionId !== null && (
        <>
          <Typography.Title level={5} style={{ marginTop: 24 }}>
            {t("activerecord.models.congregation")}
          </Typography.Title>
          <Table<InvitationCongregationRow>
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ y: 200 }}
            loading={congregations.isPending}
            dataSource={congregations.data?.data ?? []}
            columns={placeColumns<InvitationCongregationRow>(
              t("activerecord.attributes.congregation.congregation_code"),
              "congregation_code",
            )}
            rowClassName={(row) => (row.id === congregationId ? "ant-table-row-selected" : "")}
            onRow={(row) => ({
              onClick: () => setCongregationId(row.id),
              style: { cursor: "pointer" },
            })}
          />

          <Typography.Title level={5} style={{ marginTop: 24 }}>
            {t("role.management")}
          </Typography.Title>
          {/* Speaker and local coordinator need a congregation; the zone
              coordinator and web master rows appear without one. */}
          <DataTable<RoleAssignationRow>
            columns={roleColumns}
            rows={roles.data?.data}
            exportAs={`${t("role.management")} · ${[user.name, user.lastname]
              .filter(Boolean)
              .join(" ")}`}
            loading={roles.isPending || busy}
            error={roles.error}
            rowKey="role_type"
            paginated={false}
          />
        </>
      )}

      <Modal
        open={editing !== null}
        title={editing ? roleName(editing.role_type_label) : ""}
        onCancel={() => setEditing(null)}
        okText={editing?.assigned ? t("form.update") : t("invitation.invite")}
        okButtonProps={{ loading: assign.isPending }}
        onOk={() => roleForm.submit()}
        destroyOnHidden
      >
        <Form<RoleFormValues>
          form={roleForm}
          layout="vertical"
          onFinish={(values) => assign.mutate({ row: editing!, values })}
        >
          <Form.Item
            name="valid_from"
            label={t("activerecord.attributes.invitation.valid_from")}
          >
            <DatePicker style={{ width: 200 }} />
          </Form.Item>

          <Form.Item name="observer" valuePropName="checked">
            <Checkbox>{t("activerecord.attributes.invitation.observer")}</Checkbox>
          </Form.Item>

          <Form.Item
            name="observations"
            label={t("activerecord.attributes.invitation.observations")}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
}

/**
 * What the current user has sent out. The old application had a sidebar entry
 * for this that is commented out in `get_tabs`, so it sits here next to the
 * screen that creates the invitations.
 */
function SentInvitations() {
  const { t } = useTranslation();

  const invitations = useQuery({
    queryKey: ["invitation", "sent"],
    queryFn: () => apiFetch<ListResponse<SentInvitationRow>>("/invitation/sent"),
  });

  const columns = useMemo<TableProps<SentInvitationRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.invitation.name"),
        dataIndex: "name",
        width: 160,
      },
      {
        title: t("activerecord.attributes.invitation.lastname"),
        dataIndex: "lastname",
        width: 160,
      },
      {
        title: t("activerecord.attributes.invitation.email"),
        dataIndex: "email",
        width: 220,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.invitation.action_type"),
        dataIndex: "action_type_label",
        width: 140,
      },
      {
        title: t("activerecord.attributes.invitation.status"),
        dataIndex: "status_label",
        width: 140,
      },
      {
        title: t("activerecord.attributes.invitation.valid_from"),
        dataIndex: "valid_from",
        width: 130,
        render: (value: string | null) => formatDate(value),
      },
      {
        title: t("activerecord.attributes.invitation.status_date"),
        dataIndex: "created_at",
        width: 170,
        render: (value: string | null) => formatDateTime(value),
      },
    ],
    [t],
  );

  return (
    <DataTable<SentInvitationRow>
      columns={columns}
      rows={invitations.data?.data}
      loading={invitations.isPending}
      error={invitations.error}
      exportAs={t("navigation.sent_invitations")}
    />
  );
}
